import React from 'react';
import { useIntl } from 'react-intl';
import { ReactSVG } from 'react-svg';
import headerLogo from '../../assets/image/logo.svg';
import privacyNoticeStructure from './privacyNoticeStructure';

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const URL_OR_EMAIL_REGEX = /(https?:\/\/[^\s]+|[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}|(?:www\.)?[A-Z0-9.-]+\.[A-Z]{2,}(?:\/[^\s]*)?)/gi;
const SUBSECTION_TITLE_REGEX = /^\d+\.\d+(\.\d+)?\s+\S+/;
const CONTACT_COMPANY_LINE = 'Open Forest AG';

const toHref = (value) => {
  if (EMAIL_REGEX.test(value)) return `mailto:${value}`;
  if (/^https?:\/\//i.test(value)) return value;
  if (/^www\./i.test(value) || /^[A-Z0-9.-]+\.[A-Z]{2,}/i.test(value)) {
    return `https://${value}`;
  }
  return value;
};

const renderLinkedText = (text) => {
  const matches = text.match(URL_OR_EMAIL_REGEX);
  if (!matches) return text;

  const parts = [];
  let lastIndex = 0;
  let match;
  const regex = new RegExp(URL_OR_EMAIL_REGEX);

  while ((match = regex.exec(text)) !== null) {
    const [rawValue] = match;
    const start = match.index;
    const end = start + rawValue.length;
    const cleanValue = rawValue.replace(/[),.;:!?]+$/g, '');
    const suffix = rawValue.slice(cleanValue.length);

    if (start > lastIndex) {
      parts.push(text.slice(lastIndex, start));
    }

    parts.push(
      <a key={`${cleanValue}-${start}`} href={toHref(cleanValue)} target="_blank" rel="noreferrer">
        {cleanValue}
      </a>,
    );

    if (suffix) {
      parts.push(suffix);
    }

    lastIndex = end;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
};

const PrivateNotice = () => {
  const intl = useIntl();
  const noticeTitle = intl.formatMessage({ id: 'privacy.title' });
  const noticeSubtitle = intl.formatMessage({ id: 'privacy.subtitle' });
  const noticeUpdated = intl.formatMessage({ id: 'privacy.lastUpdated' });

  const renderParagraph = (paragraphText, key) => {
    if (paragraphText.startsWith('Email:')) {
      const emailText = paragraphText.replace('Email:', '').trim();
      return (
        <p key={key} className="text privacy-line privacy-contact-email">
          <strong>Email:</strong> {renderLinkedText(emailText)}
        </p>
      );
    }

    if (SUBSECTION_TITLE_REGEX.test(paragraphText)) {
      return (
        <p key={key} className="privacy-line privacy-subsection">
          {renderLinkedText(paragraphText)}
        </p>
      );
    }

    return (
      <p key={key} className="text privacy-line">
        {renderLinkedText(paragraphText)}
      </p>
    );
  };

  const renderSectionBlocks = (section) => {
    const elements = [];

    for (let index = 0; index < section.blocks.length; index += 1) {
      const block = section.blocks[index];

      if (block.type === 'list') {
        elements.push(
          <ul key={`${section.titleId}-list-${index}`} className="privacy-list">
            {block.ids.map((itemId) => (
              <li key={itemId}>
                {renderLinkedText(intl.formatMessage({ id: itemId }))}
              </li>
            ))}
          </ul>,
        );
        continue;
      }

      const paragraphText = intl.formatMessage({ id: block.id });
      if (paragraphText === CONTACT_COMPANY_LINE) {
        const lines = [paragraphText];
        let emailLine = '';
        let nextIndex = index + 1;

        while (nextIndex < section.blocks.length) {
          const nextBlock = section.blocks[nextIndex];
          if (nextBlock.type !== 'paragraph') break;
          const nextText = intl.formatMessage({ id: nextBlock.id });
          if (nextText.startsWith('Email:')) {
            emailLine = nextText;
            nextIndex += 1;
            break;
          }
          lines.push(nextText);
          nextIndex += 1;
        }

        if (emailLine) {
          elements.push(
            <div key={`contact-${section.titleId}-${index}`} className="privacy-contact-block">
              {lines.map((line, lineIndex) => (
                <p
                  key={`contact-line-${section.titleId}-${index}-${lineIndex}`}
                  className={`privacy-line ${lineIndex === 0 ? 'privacy-contact-company' : 'privacy-contact-line'}`}
                >
                  {line}
                </p>
              ))}
              {renderParagraph(emailLine, `contact-email-${section.titleId}-${index}`)}
            </div>,
          );
          index = nextIndex - 1;
          continue;
        }
      }

      elements.push(renderParagraph(paragraphText, block.id));
    }

    return elements;
  };

  return (
    <div className="terms-pages terms-pages--privacy">
      <div className="terms-pages__green-line" />
      <div className="terms-pages__wrapper">
        <div className="logo-block">
          <ReactSVG src={headerLogo} className="logo" alt="main logo" />
          <h1 className="title">{noticeTitle}</h1>
        </div>
        <div className="block_text">
          <p className=" privacy-meta">{noticeSubtitle}</p>
          <p className="text ">{noticeUpdated}</p>

          {privacyNoticeStructure.map((section) => (
            <React.Fragment key={section.titleId}>
              <h3 className={`privacy-section ${section.number === 24 ? 'privacy-section--contact' : ''}`}>
                {intl.formatMessage({ id: section.titleId })}
              </h3>
              {renderSectionBlocks(section)}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PrivateNotice;
